import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Box, Chip, Grid, Paper, TextField, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ErrorBanner from '../../common/ErrorBanner';
import { getVoiceCalls } from '../../../api/backendApi';

const statusColor = (status) => {
  const s = String(status || '').toLowerCase();
  if (s === 'in-progress') return 'success';
  if (s === 'ringing') return 'info';
  if (s === 'queued') return 'default';
  if (s === 'failed' || s === 'busy' || s === 'no-answer' || s === 'canceled') return 'error';
  return 'default';
};

const columnsBase = [
  { field: 'sid', headerName: 'Call SID', minWidth: 220, flex: 1 },
  { field: 'from', headerName: 'From', minWidth: 160, flex: 1 },
  { field: 'to', headerName: 'To', minWidth: 160, flex: 1 },
  { field: 'tenantId', headerName: 'Tenant ID', minWidth: 180, flex: 1 },
  { field: 'duration', headerName: 'Duration (s)', width: 140, type: 'number' },
  {
    field: 'status',
    headerName: 'Status',
    width: 140,
    renderCell: (params) => (
      <Chip size="small" label={params.value} color={statusColor(params.value)} />
    )
  },
];

const SummaryCard = ({ title, value }) => (
  <Paper elevation={1} sx={{ p: 2 }}>
    <Typography variant="subtitle2" color="text.secondary">{title}</Typography>
    <Typography variant="h5" sx={{ mt: 0.5 }}>{value}</Typography>
  </Paper>
);

const LiveCallsTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    try {
      setError(null);
      const data = await getVoiceCalls();
      const shaped = (Array.isArray(data) ? data : []).map((x) => ({ id: x.sid, ...x }));
      setRows(shaped);
    } catch (e) {
      setError(e?.message || 'Failed to load calls');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setInterval(load, 10000); // 10s
    load();
    return () => clearInterval(t);
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      String(r.tenantId || '').toLowerCase().includes(q) ||
      String(r.from || '').toLowerCase().includes(q) ||
      String(r.to || '').toLowerCase().includes(q)
    );
  }, [rows, query]);

  const totalActive = filtered.length;
  const avgDuration = filtered.length ? Math.round(filtered.reduce((a, b) => a + (Number(b.duration) || 0), 0) / filtered.length) : 0;
  const tenantsCount = useMemo(() => new Set(filtered.map(r => r.tenantId).filter(Boolean)).size, [filtered]);

  return (
    <Box>
      <ErrorBanner message={error} />
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}><SummaryCard title="Total Active Calls" value={totalActive} /></Grid>
        <Grid item xs={12} md={4}><SummaryCard title="Avg Duration (s)" value={avgDuration} /></Grid>
        <Grid item xs={12} md={4}><SummaryCard title="Tenants Involved" value={tenantsCount} /></Grid>
      </Grid>

      <Box sx={{ mb: 2 }}>
        <TextField size="small" label="Search tenant or number" value={query} onChange={(e) => setQuery(e.target.value)} />
      </Box>

      <div style={{ height: 520, width: '100%' }}>
        <DataGrid
          rows={filtered}
          columns={columnsBase}
          loading={loading}
          disableRowSelectionOnClick
          density="compact"
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        />
      </div>
    </Box>
  );
};

export default LiveCallsTab;
